import { ProfileCard } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { ReactComponent as LinkedInBlue } from '@backrs_ui/grow-ui/src/assets/images/linkedin-blue.svg';
import { UserProfileResponse } from '@backrs_ui/grow-ui/src/redux/profile/profile';

import { Button, Box, Divider, Typography, Stack, SxProps, Theme } from '@mui/material';

import { PublicProfileSection } from '../atoms/PublicProfileSection';

type ProfileSnapshotProps = Readonly<{
  linkedInData: UserProfileResponse['profile']['linkedInData'];
  firstName: string;
}>;

const LINKEDIN_STRINGS = appStrings.modules.profile.public.linkedIn;

export const ProfileLinkedIn = ({ linkedInData, firstName }: ProfileSnapshotProps) => {
  const linkedInUrl = linkedInData?.linkedIn_url;
  const linkedInJobs = linkedInData?.linkedIn_jobs;
  const linkedInEducation = linkedInData?.linkedIn_education;
  const linkedInSkills = linkedInData?.linkedIn_skills;

  const linkedInUrlValue = linkedInUrl?.[0]?.value;

  if (!linkedInUrlValue && !linkedInJobs?.length) {
    return null;
  }

  return (
    <Stack spacing={3} sx={containerSx}>
      <Divider />

      {/* HEADER SECTION */}
      <Box sx={headerSx}>
        <Typography variant="display-sm-medium">
          {LINKEDIN_STRINGS.deriveHeader(firstName)}
        </Typography>
        <Button
          id="profile-linkedin-btn"
          sx={buttonSx}
          startIcon={<LinkedInBlue />}
          variant="contained"
          href={linkedInUrlValue}
          target="_blank"
          rel="noopener noreferrer"
          component="a"
          hierarchy="Secondary gray"
          size="md"
        >
          <Typography sx={buttonTextSx} variant="text-sm-semibold">
            {LINKEDIN_STRINGS.mainLink}
          </Typography>
        </Button>
      </Box>

      {/* CARD SECTIONS */}
      <Box sx={cardRows}>
        {/* CAREER SECTION */}
        <PublicProfileSection
          title={LINKEDIN_STRINGS.career}
          titleSx={titleSx}
          variant={{
            type: 'CHILDREN',
            children: (
              <Box sx={cardGridSx}>
                {linkedInJobs?.map((job, index) => {
                  if (!job.job_title) {
                    return null;
                  }

                  return (
                    <ProfileCard
                      key={`${job.job_title}-${index}`}
                      title={job.job_title}
                      subtitle={job.company_name || ''}
                      supportingText={job.dates || ''}
                    />
                  );
                })}
              </Box>
            ),
          }}
        />

        {/* EDUCATION SECTION */}
        <PublicProfileSection
          title={LINKEDIN_STRINGS.education}
          titleSx={titleSx}
          variant={{
            type: 'CHILDREN',
            children: (
              <Box sx={cardGridSx}>
                {linkedInEducation?.map((education, index) => {
                  if (!education.school_name) {
                    return null;
                  }

                  return (
                    <ProfileCard
                      key={`${education.school_name}-${index}`}
                      title={education.school_name}
                      subtitle={education.certification_received || ''}
                      supportingText={education.dates || ''}
                    />
                  );
                })}
              </Box>
            ),
          }}
        />

        {/* SKILLS SECTION */}
        <Box sx={linkedInSkillsSx}>
          <PublicProfileSection
            title={LINKEDIN_STRINGS.skills}
            variant={{
              type: 'CHIP',
              chipType: 'pill',
              borderColor: 'dark',
              showMoreConfig: {
                minPerCategory: 9,
                categories: ['linkedIn'],
              },
              values:
                linkedInSkills
                  ?.filter((skill): skill is NonNullable<typeof skill> => Boolean(skill.skill))
                  .map((skill) => ({
                    name: skill.skill,
                  })) ?? [],
            }}
          />
        </Box>
      </Box>
    </Stack>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  marginTop: backrs.spacing['spacing-2xl'],
  maxWidth: '1216px',
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-md'],
  paddingX: {
    xs: backrs.spacing['spacing-xl'],
    sm: backrs.spacing['spacing-4xl'],
  },
});

const buttonSx: SxProps<Theme> = ({ backrs }) => ({
  border: `1px solid ${backrs.colors['Beige/950']}`,
  '&:active': {
    border: `1px solid ${backrs.colors['Colors/Base/Transparent']}`,
  },
});

const buttonTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Foreground/fg-secondary'],
  textTransform: 'none',
});

const cardGridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: { sm: 'wrap' },
  gap: backrs.spacing['spacing-2xl'],
  overflowX: 'auto',

  paddingX: {
    xs: backrs.spacing['spacing-xl'],
    sm: backrs.spacing['spacing-4xl'],
  },
});

const linkedInSkillsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: { sm: 'wrap' },
  gap: backrs.spacing['spacing-2xl'],
  overflowX: 'auto',

  paddingX: {
    xs: backrs.spacing['spacing-xl'],
    sm: backrs.spacing['spacing-4xl'],
  },

  maxWidth: { sm: '40vw' },
});

const cardRows: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  paddingLeft: {
    xs: backrs.spacing['spacing-xl'],
    sm: backrs.spacing['spacing-4xl'],
  },
});
